import { USER_ROLE_LABELS, PRIMARY_ADMIN_EMAIL, type UserRole } from "@bi/types";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  IconUser, IconPlus, IconX, IconAlertTriangle, IconWhatsApp, IconShield, IconClock,
} from "../components/icons";
import { accessState } from "../lib/governance";

// ── tipos ─────────────────────────────────────────────────────────────────────


type UserStatus = "active" | "inactive" | "pending";

export type ManagedUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  whatsappNumber: string | null;
  lastLoginAt: string | null;
  createdAt: string;
};

// ── helpers visuais ───────────────────────────────────────────────────────────

const ROLE_LABEL = USER_ROLE_LABELS;

const STATUS_CONFIG: Record<UserStatus, { label: string; badge: string }> = {
  active:   { label: "Ativo",    badge: "bg-tm-50 text-tm-700 border-tm-200" },
  inactive: { label: "Inativo",  badge: "bg-gray-100 text-gray-500 border-gray-200" },
  pending:  { label: "Pendente", badge: "bg-warning-50 text-warning-700 border-warning-400/40" },
};

function StatusBadge({ status }: { status: UserStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.badge}`}>
      {cfg.label}
    </span>
  );
}

const INPUT_CLS =
  "w-full text-sm border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400";

// ── formulário (criação + edição) ─────────────────────────────────────────────

type FormState = {
  name: string;
  email: string;
  password: string;
  whatsappNumber: string;
  role: UserRole;
  status: UserStatus;
};

function UserForm({
  editing,
  onSuccess,
  onCancel,
}: {
  editing: ManagedUser | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const isEdit = editing !== null;
  const { user: me } = useAuth();
  const protectedAdmin = editing?.email === PRIMARY_ADMIN_EMAIL || editing?.id === me?.id;
  const [form, setForm] = useState<FormState>({
    name: editing?.name ?? "",
    email: editing?.email ?? "",
    password: "",
    whatsappNumber: editing?.whatsappNumber ?? "",
    role: editing?.role ?? "manutencao",
    status: editing?.status ?? "active",
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return api.put(`/users/${editing.id}`, {
          name: form.name,
          whatsappNumber: form.whatsappNumber,
          role: form.role,
          status: form.status,
        });
      }
      return api.post("/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        whatsappNumber: form.whatsappNumber,
        role: form.role,
      });
    },
    onSuccess,
  });

  return (
    <div className="card p-5 border-tm-200 border-2">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-900">
          {isEdit ? `Editar usuário: ${editing.name}` : "Novo usuário"}
        </p>
        <button onClick={onCancel} className="btn-ghost p-1.5 text-gray-400">
          <IconX className="w-4 h-4" />
        </button>
      </div>

      <form
        autoComplete="off"
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="managed-user-name" className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
            <input
              id="managed-user-name" name="managed-user-name" autoComplete="off" required type="text" value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Nome completo" className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="managed-user-email" className="block text-xs font-medium text-gray-600 mb-1">E-mail *</label>
            <input
              id="managed-user-email" name="managed-user-email" autoComplete="off" required type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="usuario@empresa.com"
              disabled={isEdit}
              className={`${INPUT_CLS} ${isEdit ? "bg-gray-50 text-gray-400" : ""}`}
            />
            {isEdit && <p className="text-xs text-gray-400 mt-0.5">O e-mail não pode ser alterado.</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <span className="inline-flex items-center gap-1">
                <IconWhatsApp className="w-3.5 h-3.5 text-tm-600" /> Número WhatsApp
              </span>
            </label>
            <input
              type="tel" value={form.whatsappNumber}
              onChange={(e) => set("whatsappNumber", e.target.value)}
              placeholder="+5548999991234" className={INPUT_CLS}
            />
            <p className="text-xs text-gray-400 mt-0.5">
              Com DDI e DDD. O perfil Manutenção não acessa o canal de IA, mesmo com número cadastrado.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Perfil de acesso</label>
            <select
              value={form.email.trim().toLowerCase() === PRIMARY_ADMIN_EMAIL ? "admin" : form.role}
              disabled={protectedAdmin || form.email.trim().toLowerCase() === PRIMARY_ADMIN_EMAIL}
              aria-label="Perfil de acesso"
              onChange={(e) => set("role", e.target.value as UserRole)}
              className={INPUT_CLS}
            >
              {(["admin", "manutencao", ...(editing && !["admin", "manutencao"].includes(editing.role) ? [editing.role] : [])] as UserRole[]).map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
          </div>
        </div>

        {isEdit ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={form.status}
              disabled={protectedAdmin}
              aria-label="Status do usuário"
              onChange={(e) => set("status", e.target.value as UserStatus)}
              className={`${INPUT_CLS} max-w-xs`}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="pending">Pendente</option>
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Senha de acesso ao painel *</label>
            <input
              name="managed-user-password" autoComplete="new-password" required type="password" value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Mínimo 8 caracteres" minLength={8}
              className={`${INPUT_CLS} max-w-xs`}
            />
          </div>
        )}

        {mutation.isError && (
          <p className="text-sm text-danger-600 flex items-center gap-1.5">
            <IconAlertTriangle className="w-4 h-4" />
            {mutation.error instanceof ApiError ? mutation.error.message : "Erro ao salvar usuário"}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={mutation.isPending} className="btn-primary">
            {mutation.isPending ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar usuário"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── linha da tabela ───────────────────────────────────────────────────────────

/** "há 52 dias" com a data exata ao passar o mouse; parado há 30+ dias fica em destaque. */
function LastAccess({ user }: { user: ManagedUser }) {
  const state = accessState(user, new Date());
  const exact = user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : undefined;
  return (
    <span title={exact} className={state.stale ? "inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 font-semibold text-warning-700" : "text-gray-500"}>
      {state.stale && <IconClock className="w-3 h-3" />}
      {state.label}
    </span>
  );
}

function UserRow({
  user,
  isSelf,
  onEdit,
  onDelete,
  showChannel,
}: {
  user: ManagedUser;
  isSelf: boolean;
  onEdit: () => void;
  onDelete: () => void;
  showChannel: boolean;
}) {
  return (
    <tr className="border-t border-surface-border hover:bg-surface-muted/40">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-tm-100 flex items-center justify-center flex-shrink-0">
            <IconUser className="w-4 h-4 text-tm-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.name}{isSelf && <span className="text-xs text-gray-400 font-normal"> (você)</span>}
            </p>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
          </div>
        </div>
      </td>
      {showChannel && <td className="px-4 py-3">
        {user.whatsappNumber ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 font-mono">
            <IconWhatsApp className="w-3.5 h-3.5 text-tm-500" />
            {user.whatsappNumber}
          </span>
        ) : (
          <span className="text-xs text-gray-400 italic">sem número, não conversa</span>
        )}
      </td>}
      <td className="px-4 py-3 text-sm text-gray-600">{ROLE_LABEL[user.role]}</td>
      <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
      <td className="px-4 py-3 text-xs">
        <LastAccess user={user} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={onEdit} className="btn-ghost text-xs text-tm-600 hover:text-tm-700 px-2 py-1">
          Editar
        </button>
        <button
          onClick={onDelete}
          disabled={isSelf || user.email === PRIMARY_ADMIN_EMAIL}
          title={isSelf ? "Você não pode excluir o seu próprio usuário" : "Excluir usuário"}
          className="btn-ghost text-xs text-danger-600 hover:text-danger-700 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Excluir
        </button>
      </td>
    </tr>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function UsersPage({ showChannel = true }: { showChannel?: boolean }) {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [editing, setEditing] = useState<ManagedUser | null>(null);

  const isAdmin = me?.role === "admin";

  const { data, isLoading, error } = useQuery<{ users: ManagedUser[] }>({
    queryKey: ["users"],
    queryFn: () => api.get("/users"),
    enabled: isAdmin,
    staleTime: 30_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const refresh = () => { queryClient.invalidateQueries({ queryKey: ["users"] }); queryClient.invalidateQueries({queryKey:["user-access-log"]}); };
  const closeForm = () => { setIsAdding(false); setEditing(null); };
  const onFormSuccess = () => { closeForm(); refresh(); };

  const handleDelete = (u: ManagedUser) => {
    if (!window.confirm(`Excluir o usuário "${u.name}"? Esta ação não pode ser desfeita.`)) return;
    remove.mutate(u.id);
  };

  if (!isAdmin) {
    return (
      <div className="card p-8 text-center">
        <IconShield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-700">Acesso restrito</p>
        <p className="text-xs text-gray-400 mt-1">Apenas administradores podem gerenciar usuários.</p>
      </div>
    );
  }

  const users = (data?.users ?? []).filter(u => `${u.name} ${u.email}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()) && (roleFilter === "todos" || u.role === roleFilter) && (statusFilter === "todos" || u.status === statusFilter));
  const showForm = isAdding || editing !== null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Usuários</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Cadastre usuários e defina o acesso de cada pessoa. A conta principal permanece como Admin.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => setIsAdding(true)} className="btn-primary text-xs py-2">
            <IconPlus className="w-3.5 h-3.5" />
            Novo usuário
          </button>
        )}
      </div>

      {/* Formulário */}
      {showForm && (
        <UserForm editing={editing} onSuccess={onFormSuccess} onCancel={closeForm} />
      )}

      {remove.isError && (
        <p className="text-sm text-danger-600 flex items-center gap-1.5">
          <IconAlertTriangle className="w-4 h-4" />
          {remove.error instanceof ApiError ? remove.error.message : "Erro ao excluir usuário"}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <input aria-label="Buscar usuário por nome ou e-mail" placeholder="Buscar por nome ou e-mail" className={`${INPUT_CLS} min-w-[220px] flex-1`} value={search} onChange={e=>setSearch(e.target.value)} />
        <select aria-label="Filtrar perfil" className={`${INPUT_CLS} sm:w-48`} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}><option value="todos">Todos os perfis</option>{Object.entries(ROLE_LABEL).map(([key,label])=><option key={key} value={key}>{label}</option>)}</select>
        <select aria-label="Filtrar status" className={`${INPUT_CLS} sm:w-40`} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="todos">Todos os status</option><option value="active">Ativos</option><option value="inactive">Inativos</option><option value="pending">Pendentes</option></select>
      </div>
      {/* Tabela */}
      {isLoading ? (
        <div className="card p-5 animate-pulse space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}
        </div>
      ) : error ? (
        <div className="card p-6 text-center">
          <IconAlertTriangle className="w-7 h-7 text-danger-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            {error instanceof ApiError ? error.message : "Não foi possível carregar os usuários."}
          </p>
        </div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center border-2 border-dashed border-surface-border rounded-xl">
          <IconUser className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nenhum usuário cadastrado</p>
          <p className="text-xs text-gray-400 mt-1">Clique em "Novo usuário" para começar.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[850px]">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 bg-surface-muted/50">
                <th className="px-4 py-2.5">Usuário</th>
                {showChannel && <th className="px-4 py-2.5">WhatsApp</th>}
                <th className="px-4 py-2.5">Perfil</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Último acesso</th>
                <th className="px-4 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  showChannel={showChannel}
                  user={u}
                  isSelf={u.id === me?.id}
                  onEdit={() => { setIsAdding(false); setEditing(u); }}
                  onDelete={() => handleDelete(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Nota explicativa */}
      {showChannel && <div className="card p-5 border-info-100">
        <div className="flex items-start gap-3">
          <IconWhatsApp className="w-4 h-4 text-tm-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-1">Como liberar o chat por WhatsApp</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              A IA só responde usuários ativos com perfil autorizado e número cadastrado no campo <strong>WhatsApp</strong> de um usuário.
              Use o formato internacional <code className="bg-gray-100 px-1 rounded">+5548999991234</code> (DDI + DDD + número).
              O perfil Manutenção não acessa este canal. Para revogar um acesso, inative o usuário ou remova o número.
            </p>
          </div>
        </div>
      </div>}
    </div>
  );
}
